import React, { useCallback, useEffect, useState } from 'react';
import { Form, Spinner } from 'react-bootstrap';
import { toast } from 'sonner';
import { Share2 } from 'lucide-react';

import { getPriorsOptOut, setPriorsOptOut } from '../../../services/MatchingService';

/**
 * "Dela anonym statistik" — the per-tenant global-priors opt-out control
 * (design §4.6b, Phase 4 item 3). Gated by 'global_priors' by the parent (and
 * the endpoint, which 403s when absent).
 *
 * The toggle is phrased as SHARING (opted_out inverted): on = contribute
 * anonymous, aggregated, k-anonymised booking patterns to the cross-tenant
 * evidence layer; off = opt out of both aggregation and lookup.
 *
 * Graceful by contract: 403/404 hides the control entirely.
 */

export default function PriorsOptOutControl({ companyId }) {
    const [loading, setLoading] = useState(true);
    const [hidden, setHidden] = useState(false);
    const [optedOut, setOptedOut] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!companyId) return undefined;
        let cancelled = false;
        setLoading(true);
        getPriorsOptOut(companyId)
            .then((data) => {
                if (cancelled) return;
                setOptedOut(!!data?.opted_out);
                setHidden(false);
            })
            .catch(() => {
                if (cancelled) return;
                // Any read failure (feature absent or transient) → hide; this is
                // a peripheral control, never worth an error box.
                setHidden(true);
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [companyId]);

    const handleToggle = useCallback(async (share) => {
        const nextOptedOut = !share;
        setBusy(true);
        try {
            await setPriorsOptOut(companyId, nextOptedOut);
            setOptedOut(nextOptedOut);
            toast.success(nextOptedOut
                ? 'Du delar inte längre anonym statistik.'
                : 'Tack — du bidrar nu med anonym statistik.');
        } catch (err) {
            toast.error(err?.response?.data?.message || err.message || 'Kunde inte ändra inställningen');
        } finally {
            setBusy(false);
        }
    }, [companyId]);

    if (hidden || loading) return null;

    return (
        <div className="insights-box" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <Share2 size={16} style={{ color: '#6b7280', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.875rem', fontWeight: 600, color: '#1f2937' }}>Dela anonym statistik</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    Bidra med anonymiserade, aggregerade bokföringsmönster så att andra företag kan få bättre förslag.
                    Ingen enskild transaktion delas.
                </div>
            </div>
            <Form.Check
                type="switch"
                id="priors-opt-out"
                checked={!optedOut}
                disabled={busy}
                onChange={(e) => handleToggle(e.target.checked)}
                label={busy ? <Spinner animation="border" size="sm" /> : (optedOut ? 'Av' : 'På')}
            />
        </div>
    );
}
